import React from "react";
import { AssessmentProgram } from "@lib/ShiOneClient";
import { assessmentPrograms } from "@features/assessments-feature/constants";
import { AutoAwesome, DeveloperBoard, Security } from "@mui/icons-material";
import AssessmentView from "@features/assessments-feature/components/AssessmentView";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import InsightsIcon from "@mui/icons-material/Insights";
import GridViewIcon from "@mui/icons-material/GridView";
import CloudMatrixPage from "@features/assessments-feature/components/cloud-matrix/CloudMatrixPage";
import MicrosoftInsightsPage from "@features/assessments-feature/components/microsoft-insights/MicrosoftInsightsPage";

export interface AssessmentViewProps {
  id: AssessmentProgram;
  program: string;
  name: string;
  airtableProgramName: string;
  description: string;
  icon: React.ReactElement<any>;
  component: React.ReactNode;
  url: string;
  promoPic?: string;
  promoUrl?: string;
}

const assessmentViewsMap: Map<string, AssessmentViewProps> = new Map<
  string,
  AssessmentViewProps
>([
  [
    assessmentPrograms.spr,
    {
      id: AssessmentProgram.SecurityPostureReview,
      program: assessmentPrograms.spr,
      name: "Security Posture Review",
      airtableProgramName: "Security Posture Review",
      description:
        "Aims to map your security architecture, identify gaps, highlight overlaps, and reveal opportunities for consolidation in your security posture.",
      icon: <Security />,
      url: "/assessments/" + assessmentPrograms.spr,
      component: <AssessmentView program={assessmentPrograms.spr} />,
      promoPic:
        "https://shionestorage.blob.core.windows.net/shione/SecurityPostureReview.webp",
      promoUrl:
        "https://shionestorage.blob.core.windows.net/shione/SecurityPostureReview-OnePager.pdf",
    },
  ],
  [
    assessmentPrograms.sprV2,
    {
      id: AssessmentProgram.SecurityPostureReviewV2,
      program: assessmentPrograms.sprV2,
      name: "Security Posture Review",
      airtableProgramName: "Security Posture Review v2",
      description:
        "Aims to map your security architecture, identify gaps, highlight overlaps, and reveal opportunities for consolidation in your security posture.",
      icon: <Security />,
      url: "/assessments/" + assessmentPrograms.sprV2,
      component: <AssessmentView program={assessmentPrograms.sprV2} />,
      promoPic:
        "https://shionestorage.blob.core.windows.net/shione/SecurityPostureReview.webp",
      promoUrl:
        "https://shionestorage.blob.core.windows.net/shione/SecurityPostureReview-OnePager.pdf",
    },
  ],
  [
    assessmentPrograms.aiReadiness,
    {
      id: AssessmentProgram.AiReadiness,
      program: assessmentPrograms.aiReadiness,
      name: "AI Readiness",
      airtableProgramName: "AI Readiness",
      description:
        "Evaluates your current systems and processes to identify potential gaps and deliver actionable solutions for effective AI adoption.",
      icon: <AutoAwesome />,
      url: "/assessments/" + assessmentPrograms.aiReadiness,
      component: <AssessmentView program={assessmentPrograms.aiReadiness} />,
      promoPic:
        "https://shionestorage.blob.core.windows.net/shione/AIReadiness.webp",
      promoUrl:
        "https://shionestorage.blob.core.windows.net/shione/AIReadiness-OnePager.pdf",
    },
  ],
  [
    assessmentPrograms.dopr,
    {
      id: AssessmentProgram.DevOpsPostureReview,
      program: assessmentPrograms.dopr,
      name: "DevOps Posture Review",
      airtableProgramName: "DevOps Posture Review",
      description:
        "Identifies potential gaps in your tooling & processes, and analyzes your maturity against indicators of high performing teams to recommend key areas of focus and help prioritize DevOps initiatives.",
      icon: <DeveloperBoard />,
      url: "/assessments/" + assessmentPrograms.dopr,
      component: <AssessmentView program={assessmentPrograms.dopr} />,
      promoPic:
        "https://shionestorage.blob.core.windows.net/shione/DevOpsPostureReview.webp",
      promoUrl:
        "https://shionestorage.blob.core.windows.net/shione/DevOpsPostureReview-1Pager.pdf",
    },
  ],
  [
    assessmentPrograms.awsMaturity,
    {
      id: AssessmentProgram.AwsMaturity,
      program: assessmentPrograms.awsMaturity,
      name: "AWS Maturity",
      airtableProgramName: "AWS Maturity",
      description:
        "Grounded in the AWS Well Architected Framework, this assessment collects a baseline of how your organization is managing your AWS environment today & outlines which areas to prioritize. ",
      icon: <CloudDoneIcon />,
      url: "/assessments/" + assessmentPrograms.awsMaturity,
      component: <AssessmentView program={assessmentPrograms.awsMaturity} />,
      promoPic:
        "https://shionestorage.blob.core.windows.net/shione/AwsMaturityAssessment.webp",
      promoUrl:
        "https://shionestorage.blob.core.windows.net/shione/DevOpsPostureReview-1Pager.pdf",
    },
  ],
  [
    assessmentPrograms.tpx,
    {
      id: AssessmentProgram.Tpx,
      program: assessmentPrograms.tpx,
      name: "TPX Assessment",
      airtableProgramName: "TPX",
      description:
        "Transforms Thought Partnership Exchange transcripts into curated VSEM insights and prioritized recommendations powered by AI.",
      icon: <InsightsIcon />,
      url: "/assessments/" + assessmentPrograms.tpx,
      component: <AssessmentView program={assessmentPrograms.tpx} />,
    },
  ],
  [
    assessmentPrograms.cloudMatrix,
    {
      id: AssessmentProgram.CloudMatrix,
      program: assessmentPrograms.cloudMatrix,
      name: "Cloud Matrix",
      airtableProgramName: "Cloud Matrix",
      description:
        "Provides comprehensive cloud security posture management insights to help identify and remediate risks across your cloud environments.",
      icon: <GridViewIcon />,
      url: "/assessments/" + assessmentPrograms.cloudMatrix,
      component: <CloudMatrixPage />,
    },
  ],
  [
    assessmentPrograms.microsoftInsights,
    {
      id: AssessmentProgram.MicrosoftInsights,
      program: assessmentPrograms.microsoftInsights,
      name: "Microsoft Insights",
      airtableProgramName: "Microsoft Insights",
      description: "Details a unified view of your Microsoft estate, showing license ownership, usage, and where activity exceeds assigned capabilities.",
      icon: <AssessmentIcon />,
      url: "/assessments/" + assessmentPrograms.microsoftInsights,
      component: <MicrosoftInsightsPage />
    }
  ]
]);
export default assessmentViewsMap;
